import type { FC, ReactNode } from "react";

export interface EmptyStateProps {
  /**
   * Icon or illustration to display
   */
  icon?: ReactNode;

  /**
   * Main title text
   */
  title: string;

  /**
   * Description text
   */
  description?: string;

  /**
   * Optional action button or element
   */
  action?: ReactNode;

  /**
   * Show Arti robot companion instead of icon
   */
  showRobot?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Visual density
   * @default 'md'
   */
  size?: "sm" | "md" | "lg";
}

/**
 * EmptyState Component
 *
 * Displays a friendly empty state with optional icon, description, and action.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={<QueueIcon />}
 *   title="No tasks queued"
 *   description="Tasks will appear here when they're added to the queue"
 * />
 * ```
 */
export const EmptyState: FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  showRobot = false,
  className = "",
  size = "md",
}) => {
  const sizeClasses: Record<NonNullable<EmptyStateProps["size"]>, string> = {
    lg: "px-6 py-8",
    md: "px-5 py-6",
    sm: "px-4 py-5",
  };
  const iconClasses: Record<NonNullable<EmptyStateProps["size"]>, string> = {
    lg: "mb-4 h-12 w-12",
    md: "mb-3 h-10 w-10",
    sm: "mb-2.5 h-8 w-8",
  };
  const emptyMarkClasses: Record<
    NonNullable<EmptyStateProps["size"]>,
    string
  > = {
    lg: "mb-5 text-3xl gap-2",
    md: "mb-4 text-2xl gap-1.5",
    sm: "mb-3 text-xl gap-1",
  };
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-none border-2 border-dashed border-slate-300/80 bg-slate-50/70 text-center dark:border-slate-700 dark:bg-arti-panel ${sizeClasses[size]} ${className} `
        .trim()
        .replace(/\s+/g, " ")}
    >
      {showRobot && (
        <div
          className={`flex items-center font-mono font-bold text-slate-600/40 dark:text-slate-600 ${emptyMarkClasses[size]}`}
        >
          <span>[</span>
          <span className="text-emerald-500/30 dark:text-emerald-500/20">
            —
          </span>
          <span>]</span>
        </div>
      )}
      {!showRobot && icon && (
        <div
          className={`${iconClasses[size]} flex items-center justify-center rounded-none border border-slate-300 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-arti-background dark:text-white/30`}
        >
          {icon}
        </div>
      )}
      <h3
        className={`mb-1 font-mono font-bold uppercase tracking-[0.18em] text-slate-700 dark:text-white/80 ${size === "sm" ? "text-[10px]" : "text-[11px]"}`}
      >
        {title}
      </h3>
      {description && (
        <p
          className={`max-w-sm text-slate-500 dark:text-white/50 ${size === "sm" ? "text-xs" : "text-xs leading-snug"}`}
        >
          {description}
        </p>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
};

export const NoDataIcon: FC = () => (
  <svg
    className="h-8 w-8"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

export const NodesEmptyState: FC<{ className?: string }> = ({ className }) => (
  <EmptyState
    showRobot
    title="No nodes registered"
    description="Executor nodes will appear here once they register with the orchestrator"
    className={className}
  />
);

export const TasksEmptyState: FC<{ className?: string }> = ({ className }) => (
  <EmptyState
    showRobot
    title="No tasks found"
    description="Tasks will appear here once they are created and executed"
    className={className}
  />
);
